import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { History, GitCompareArrows, RotateCcw, X, Loader2, Check } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Publication Versions manager — list, compare, restore.
 *
 * Props: `apiPrefix` — "/me" (default, doctor) or "/admin" (admin viewing another doctor).
 *        `doctorId` — required if apiPrefix is admin.
 *        `onRestore` — callback fired after successful restore.
 */
export default function PublicationVersions({ apiPrefix = "/me", doctorId, onRestore }) {
    const base = apiPrefix === "/admin" ? `/admin/publish/${doctorId}` : "/me/publish";
    const [versions, setVersions] = useState([]);
    const [selA, setSelA] = useState("");
    const [selB, setSelB] = useState("");
    const [diff, setDiff] = useState(null);
    const [comparing, setComparing] = useState(false);
    const [restoring, setRestoring] = useState(null);
    const [confirmRestore, setConfirmRestore] = useState(null);

    async function load() {
        try {
            const r = await api.get(`${base}/versions`);
            const list = r.data || [];
            setVersions(list);
            // Preselect the newest and one-before-newest
            if (list.length >= 2) {
                setSelA(String(list[1].version_number));
                setSelB(String(list[0].version_number));
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load versions");
        }
    }
    useEffect(() => { load(); }, []);

    async function compare() {
        if (!selA || !selB || selA === selB) {
            toast.error("Pick two DIFFERENT versions");
            return;
        }
        setComparing(true);
        setDiff(null);
        try {
            const r = await api.get(`${base}/versions/${selA}/compare/${selB}`);
            setDiff(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to compare");
        } finally { setComparing(false); }
    }

    async function restore(vnum) {
        setRestoring(vnum);
        try {
            const r = await api.post(`${base}/versions/${vnum}/restore`);
            toast.success(`Restored v${vnum} → new v${r.data.new_version_number}`);
            setConfirmRestore(null);
            await load();
            if (onRestore) onRestore(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed");
        } finally { setRestoring(null); }
    }

    return (
        <div data-testid="publication-versions">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-[15px] font-semibold text-[#0F172A] flex items-center gap-2">
                    <History size={16} className="text-[#3A80F7]" /> Publication history
                </h2>
                <div className="text-[11px] text-[#94A3B8]">
                    {versions.length} version{versions.length === 1 ? "" : "s"}
                </div>
            </div>

            {/* Compare picker */}
            {versions.length >= 2 && (
                <div className="ddi-card p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="versions-compare-picker">
                    <div className="flex-1 min-w-[160px]">
                        <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1 block">Old</label>
                        <Select value={selA} onValueChange={setSelA}>
                            <SelectTrigger data-testid="versions-select-a"><SelectValue placeholder="Choose version" /></SelectTrigger>
                            <SelectContent>
                                {versions.map((v) => (
                                    <SelectItem key={v.version_number} value={String(v.version_number)}>
                                        v{v.version_number} · {new Date(v.published_at).toLocaleDateString()}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex-1 min-w-[160px]">
                        <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1 block">New</label>
                        <Select value={selB} onValueChange={setSelB}>
                            <SelectTrigger data-testid="versions-select-b"><SelectValue placeholder="Choose version" /></SelectTrigger>
                            <SelectContent>
                                {versions.map((v) => (
                                    <SelectItem key={v.version_number} value={String(v.version_number)}>
                                        v{v.version_number} · {new Date(v.published_at).toLocaleDateString()}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={compare} disabled={comparing} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="versions-compare-btn">
                        <GitCompareArrows size={14} className="mr-2" /> {comparing ? "Comparing…" : "Compare"}
                    </Button>
                </div>
            )}

            {/* Diff panel */}
            {diff && (
                <div className="ddi-card p-4 mb-4" data-testid="versions-diff-panel">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[13px] text-[#334155]">
                            Comparing <span className="font-mono font-semibold">v{diff.old.version_number}</span> → <span className="font-mono font-semibold">v{diff.new.version_number}</span>
                        </div>
                        <button onClick={() => setDiff(null)} className="text-[#94A3B8] hover:text-[#DC2626] p-1"><X size={14} /></button>
                    </div>
                    {!diff.diff.changed ? (
                        <div className="py-4 text-center text-[13px] text-[#64748B] flex items-center justify-center gap-2">
                            <Check size={14} className="text-[#16A34A]" /> No differences between these two versions.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {diff.diff.text_changes.length > 0 && (
                                <div data-testid="diff-text-changes">
                                    <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-2">Text fields changed ({diff.diff.text_changes.length})</div>
                                    <ul className="space-y-2">
                                        {diff.diff.text_changes.map((c) => (
                                            <li key={c.path} className="rounded-lg border border-[#E5E9F2] p-3">
                                                <div className="text-[11px] font-mono text-[#3A80F7] mb-1">{c.path}</div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
                                                    <div className="bg-[#FEE2E2] rounded p-2 text-[#7F1D1D]"><span className="text-[10px] uppercase font-semibold opacity-70">Old</span><div className="mt-0.5 line-clamp-4 whitespace-pre-wrap">{c.old || <em className="opacity-60">empty</em>}</div></div>
                                                    <div className="bg-[#E7F7EE] rounded p-2 text-[#166534]"><span className="text-[10px] uppercase font-semibold opacity-70">New</span><div className="mt-0.5 line-clamp-4 whitespace-pre-wrap">{c.new || <em className="opacity-60">empty</em>}</div></div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {diff.diff.list_changes.length > 0 && (
                                <div data-testid="diff-list-changes">
                                    <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-2">Lists changed ({diff.diff.list_changes.length})</div>
                                    <ul className="space-y-2">
                                        {diff.diff.list_changes.map((c) => (
                                            <li key={c.path} className="rounded-lg border border-[#E5E9F2] p-3 text-[12px]">
                                                <div className="font-mono text-[#3A80F7] text-[11px] mb-1">{c.path}</div>
                                                <div className="text-[#334155]">
                                                    {c.old_count} → {c.new_count} {c.label.toLowerCase()}s
                                                </div>
                                                {c.added.length > 0 && <div className="text-[#16A34A] mt-1">+ {c.added.join(", ")}</div>}
                                                {c.removed.length > 0 && <div className="text-[#DC2626] mt-1">− {c.removed.join(", ")}</div>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {diff.diff.kind_changes.length > 0 && (
                                <div data-testid="diff-kind-changes">
                                    <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-2">Item counts changed</div>
                                    <ul className="space-y-1 text-[12px]">
                                        {diff.diff.kind_changes.map((c) => (
                                            <li key={c.kind} className="flex items-center gap-2">
                                                <span className="font-mono text-[#334155]">{c.kind}</span>
                                                <span className="text-[#94A3B8]">{c.old_count} →</span>
                                                <span className={c.delta > 0 ? "text-[#16A34A] font-semibold" : "text-[#DC2626] font-semibold"}>
                                                    {c.new_count}
                                                </span>
                                                <span className="text-[10px] text-[#94A3B8]">({c.delta > 0 ? "+" : ""}{c.delta})</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Version list with restore action */}
            <div className="ddi-card overflow-hidden" data-testid="publish-versions">
                {versions.length === 0 ? (
                    <div className="p-6 text-[13px] text-[#94A3B8] text-center">Not published yet.</div>
                ) : (
                    <table className="w-full text-[13px]">
                        <thead className="bg-[#F5F7FB] text-[#334155]">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Version</th>
                                <th className="text-left px-4 py-3 font-semibold">Template</th>
                                <th className="text-left px-4 py-3 font-semibold">Slug</th>
                                <th className="text-left px-4 py-3 font-semibold">Published</th>
                                <th className="text-left px-4 py-3 font-semibold">Source</th>
                                <th className="text-right px-4 py-3 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {versions.map((v) => {
                                const isLatest = v.version_number === Math.max(...versions.map((x) => x.version_number));
                                return (
                                    <tr key={v.version_number} className="border-t border-[#EEF1F6] hover:bg-[#F9FAFC]" data-testid={`version-row-${v.version_number}`}>
                                        <td className="px-4 py-3 font-mono">
                                            v{v.version_number}
                                            {isLatest && <span className="ml-2 ddi-chip ddi-chip-success text-[9px]">Current</span>}
                                        </td>
                                        <td className="px-4 py-3 capitalize">{(v.template_code || "").replaceAll("_", " ")}</td>
                                        <td className="px-4 py-3 font-mono text-[#3A80F7] text-[12px]">{v.slug}</td>
                                        <td className="px-4 py-3 text-[11px] text-[#64748B]">
                                            {v.published_at ? new Date(v.published_at).toLocaleString() : "-"}
                                        </td>
                                        <td className="px-4 py-3 text-[11px] text-[#64748B]">
                                            {v.restored_from_version ? (
                                                <span className="ddi-chip ddi-chip-primary text-[10px]">Restored from v{v.restored_from_version}</span>
                                            ) : (
                                                <span className="text-[#94A3B8]">Original</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {!isLatest && (
                                                <button
                                                    onClick={() => setConfirmRestore(v)}
                                                    disabled={restoring === v.version_number}
                                                    className="text-[12px] text-[#3A80F7] font-semibold hover:underline inline-flex items-center gap-1"
                                                    data-testid={`version-restore-${v.version_number}`}
                                                >
                                                    {restoring === v.version_number ? (
                                                        <Loader2 size={11} className="animate-spin" />
                                                    ) : (
                                                        <RotateCcw size={11} />
                                                    )}
                                                    Restore
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Restore confirmation modal */}
            {confirmRestore && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="restore-confirm-modal">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5">
                        <div className="flex items-start gap-3 mb-3">
                            <div className="h-10 w-10 rounded-lg bg-[#FEF3C7] text-[#B45309] flex items-center justify-center">
                                <RotateCcw size={18} />
                            </div>
                            <div>
                                <div className="text-[15px] font-bold text-[#0F172A]">Restore version v{confirmRestore.version_number}?</div>
                                <div className="text-[12px] text-[#64748B] mt-1">
                                    This creates a NEW version (v{Math.max(...versions.map((x) => x.version_number)) + 1}) with content copied from v{confirmRestore.version_number}. Your existing history stays intact. Your public page will reflect the restored content immediately.
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setConfirmRestore(null)} data-testid="restore-cancel">Cancel</Button>
                            <Button
                                onClick={() => restore(confirmRestore.version_number)}
                                disabled={restoring !== null}
                                className="bg-[#3A80F7] hover:bg-[#2D6BE0]"
                                data-testid="restore-confirm"
                            >
                                {restoring !== null ? <Loader2 size={14} className="animate-spin mr-2" /> : <RotateCcw size={14} className="mr-2" />}
                                Yes, restore
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
